import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  FolderIcon,
  ClockIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import Layout from '../../components/layout/Layout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
// import GanttChart from '../../components/analytics/GanttChart'; // Disabled due to dependency issues
import SimpleGantt from '../../components/analytics/SimpleGantt';
// import MindMap from '../../components/analytics/MindMap'; // Disabled due to dependency issues
import SimpleMindMap from '../../components/analytics/SimpleMindMap';
import AdvancedCharts from '../../components/analytics/AdvancedCharts';
import SimpleCharts from '../../components/analytics/SimpleCharts';
import { analyticsAPI } from '../../services/api';
import toast from 'react-hot-toast';

// Simple Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error('Analytics component error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

const AdminAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeView, setActiveView] = useState<'overview' | 'projects' | 'teams' | 'trends' | 'gantt' | 'mindmap' | 'charts'>('overview');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      const data = await analyticsAPI.getOverview();
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'TODO': return 'bg-gray-100 text-gray-800';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800';
      case 'REVIEW': return 'bg-yellow-100 text-yellow-800';
      case 'DONE': return 'bg-green-100 text-green-800';
      case 'PLANNING': return 'bg-purple-100 text-purple-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'ON_HOLD': return 'bg-orange-100 text-orange-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'HIGH': return <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />;
      case 'NORMAL': return <InformationCircleIcon className="w-4 h-4 text-blue-500" />;
      case 'LOW': return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
      default: return <InformationCircleIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Analytics Dashboard</h1>
            <p className="text-sm text-gray-500">
              Comprehensive insights into all projects, teams, and tasks across the organization
            </p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: ChartBarIcon },
              { id: 'charts', name: 'Advanced Charts', icon: ChartBarIcon },
              { id: 'gantt', name: 'Timeline', icon: CalendarIcon },
              { id: 'mindmap', name: 'Organization', icon: UserGroupIcon },
              { id: 'projects', name: 'Projects', icon: FolderIcon },
              { id: 'teams', name: 'Teams', icon: UserGroupIcon },
              { id: 'trends', name: 'Trends', icon: ArrowTrendingUpIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center space-x-2 ${
                  activeView === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {activeView === 'overview' && analytics && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <div className="flex items-center">
                    <FolderIcon className="w-8 h-8 text-blue-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-blue-600">Total Projects</p>
                      <p className="text-2xl font-bold text-blue-900">
                        {analytics.overview?.totalProjects || 0}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 p-6 rounded-lg">
                  <div className="flex items-center">
                    <ClockIcon className="w-8 h-8 text-green-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-600">Total Tasks</p>
                      <p className="text-2xl font-bold text-green-900">
                        {analytics.overview?.totalTasks || 0}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 p-6 rounded-lg">
                  <div className="flex items-center">
                    <UserGroupIcon className="w-8 h-8 text-purple-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-purple-600">Total Teams</p>
                      <p className="text-2xl font-bold text-purple-900">
                        {analytics.overview?.totalTeams || 0}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 p-6 rounded-lg">
                  <div className="flex items-center">
                    <ArrowTrendingUpIcon className="w-8 h-8 text-yellow-600" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-yellow-600">Total Users</p>
                      <p className="text-2xl font-bold text-yellow-900">
                        {analytics.overview?.totalUsers || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Distributions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Project Status Distribution */}
                {analytics.distributions?.projectsByStatus && (
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Project Status Distribution</h3>
                    <div className="space-y-3">
                      {analytics.distributions.projectsByStatus.map((item: any) => (
                        <div key={item.status} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status)}`}>
                              {item.status.replace('_', ' ')}
                            </span>
                          </div>
                          <span className="text-lg font-semibold text-gray-900">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Task Status Distribution */}
                {analytics.distributions?.tasksByStatus && (
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Task Status Distribution</h3>
                    <div className="space-y-3">
                      {analytics.distributions.tasksByStatus.map((item: any) => (
                        <div key={item.status} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status)}`}>
                              {item.status.replace('_', ' ')}
                            </span>
                          </div>
                          <span className="text-lg font-semibold text-gray-900">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Priority Distributions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Task Priority Distribution */}
                {analytics.distributions?.tasksByPriority && (
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Task Priority Distribution</h3>
                    <div className="space-y-3">
                      {analytics.distributions.tasksByPriority.map((item: any) => (
                        <div key={item.priority} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {getPriorityIcon(item.priority)}
                            <span className="text-sm font-medium text-gray-700">{item.priority}</span>
                          </div>
                          <span className="text-lg font-semibold text-gray-900">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Project Priority Distribution */}
                {analytics.distributions?.projectsByPriority && (
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Project Priority Distribution</h3>
                    <div className="space-y-3">
                      {analytics.distributions.projectsByPriority.map((item: any) => (
                        <div key={item.priority} className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {getPriorityIcon(item.priority)}
                            <span className="text-sm font-medium text-gray-700">{item.priority}</span>
                          </div>
                          <span className="text-lg font-semibold text-gray-900">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Recent Activity */}
              {analytics.trends?.recentActivity && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Recent Projects */}
                  {analytics.trends.recentActivity.recentProjects && (
                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Projects</h3>
                      <div className="space-y-3">
                        {analytics.trends.recentActivity.recentProjects.slice(0, 5).map((project: any) => (
                          <div key={project.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900">{project.name}</p>
                              <p className="text-sm text-gray-500">
                                by {project.creator.firstName} {project.creator.lastName}
                              </p>
                            </div>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(project.status)}`}>
                              {project.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Tasks */}
                  {analytics.trends.recentActivity.recentTasks && (
                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Tasks</h3>
                      <div className="space-y-3">
                        {analytics.trends.recentActivity.recentTasks.slice(0, 5).map((task: any) => (
                          <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900">{task.title}</p>
                              <p className="text-sm text-gray-500">
                                {task.project ? (
                                  <span className="flex items-center space-x-1">
                                    <div 
                                      className="w-2 h-2 rounded-full" 
                                      style={{ backgroundColor: task.project.color || '#6B7280' }}
                                    />
                                    <span>{task.project.name}</span>
                                  </span>
                                ) : (
                                  'No Project'
                                )}
                              </p>
                            </div>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                              {task.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {activeView === 'teams' && analytics?.performance?.teamPerformance && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Team Performance</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {analytics.performance.teamPerformance.map((team: any) => (
                    <div key={team.id} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3 mb-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: team.color || '#6B7280' }}
                        />
                        <h4 className="font-medium text-gray-900">{team.name}</h4>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Projects:</span>
                          <span className="font-medium">{team._count.projects}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Tasks:</span>
                          <span className="font-medium">{team._count.tasks}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Members:</span>
                          <span className="font-medium">{team._count.members}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Task Completion:</span>
                          <span className="font-medium">{team.taskCompletionRate?.toFixed(1) || 0}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Project Completion:</span>
                          <span className="font-medium">{team.projectCompletionRate?.toFixed(1) || 0}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Add more views for projects and trends */}
          {activeView === 'projects' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="text-center py-12">
                <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Project Analytics</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Detailed project analytics will be displayed here.
                </p>
              </div>
            </motion.div>
          )}

          {activeView === 'charts' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <ErrorBoundary fallback={<SimpleCharts analytics={analytics} />}>
                <SimpleCharts analytics={analytics} />
              </ErrorBoundary>
            </motion.div>
          )}

          {activeView === 'gantt' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <ErrorBoundary fallback={<SimpleGantt />}>
                <SimpleGantt />
              </ErrorBoundary>
            </motion.div>
          )}

          {activeView === 'mindmap' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <ErrorBoundary fallback={<SimpleMindMap />}>
                <SimpleMindMap />
              </ErrorBoundary>
            </motion.div>
          )}

          {activeView === 'trends' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="text-center py-12">
                <ArrowTrendingUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Trend Analytics</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Trend analysis and forecasting will be displayed here.
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminAnalytics;
